'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useModal } from 'lib/components/Modal/Modal'

import { useUserApi } from '@/api/useUserApi'
import { useMounted } from '@/hooks/usePageLoad'
import useSessionStore from '@/stores/useSessionStore'

export interface ShipplayAuth {
  isLoggedIn: boolean
  firstName: string | undefined
  activeShipments: number
  onLogIn: () => void
  onLogOut: () => void
  onSignUp: () => void
}

/**
 * Auth state + handlers for the Ship&Play (Kettle) SiteNav / SiteUserBar / NavSheet.
 * Mirrors the wiring in useSiteHeader so the login-modal + session clear behave the
 * same regardless of which header the user sees.
 */
export function useShipplayAuth(): ShipplayAuth {
  const mounted = useMounted()
  const authToken = useSessionStore((s) => s.authToken)
  const clearSession = useSessionStore((s) => s.clearSession)
  const openDialog = useModal((s) => s.openDialog)
  const queryClient = useQueryClient()
  const userQuery = useUserApi(!!authToken)

  const isLoggedIn = mounted && !!authToken
  const firstName = mounted ? userQuery.data?.firstName : undefined
  const activeShipments = userQuery.data?.activeShipments ?? 0

  const onLogIn = useCallback((): void => {
    openDialog('login-modal')
  }, [openDialog])

  const onSignUp = useCallback((): void => {
    openDialog('signup-modal')
  }, [openDialog])

  const onLogOut = useCallback((): void => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE')
    }
    queryClient.clear()
    clearSession()
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [queryClient, clearSession])

  return { isLoggedIn, firstName, activeShipments, onLogIn, onLogOut, onSignUp }
}
